import { CalendarIcon, ChevronRight, Info, Plus, X } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useParams } from "react-router-dom";
import {
  BulkCaseEventCreateRequest,
  CommitteeType,
  CommitteeTypeLabelMap,
  EventEntityType,
  EventType,
  LEAType,
  LEATypeLabelMap,
} from "../../../api/events";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import ButtonV2 from "../../../components/ui/button/v2";
import SelectDropdownV2 from "../../../components/ui/selectdropdown/v2";
import MultiSelectSummaryDropdown from "../../../components/ui/selectdropdown/v2/MultiSelectSummaryDropdown";
import { OptionNameValuePair } from "../../../lib/interfaces/types";
import { cn } from "../../../lib/utils";
import { getEventTypeLabel, STAGE_META } from "../constants/addEventModal";
import { selectAllEvents } from "../logic/slice";
import { addCaseEvents } from "../logic/thunks";
import { getAccountProductInfo } from "../../creditAccounts/logic/slice";
import { getAllPartyPerpetrators } from "../../stakeholder/logic/stakeholderSlice";
import { isCreditAreaOfFraud } from "../../caseinfo/logic/Slice";

export type AddEventModalMode = "Add" | "Edit";

export const SYSTEM_ONLY_EVENT_TYPES: EventType[] = [
  EventType.CASE_OPENED,
  EventType.CASE_ASSIGNED_TO_INVESTIGATOR,
  EventType.CASE_DEALING_OFFICER_ASSIGNED,
  EventType.IO_APPOINTING_AUTHORITY_ASSIGNED,
];

interface AddEventModalProps {
  mode?: AddEventModalMode;
  onClose: () => void;
  disableStageSelect?: boolean;
  disableEntitySelect?: boolean;
  readOnlyNote?: boolean;
  initialData?: {
    eventType?: EventType;
    note?: string;
    events?: Array<{ entity_id: string; date: string }>;
  };
}

export const AddEventModal: React.FC<AddEventModalProps> = ({
  mode = "Add",
  onClose,
  disableStageSelect = false,
  disableEntitySelect = false,
  readOnlyNote = false,
  initialData,
}) => {
  const auth = useAuth();
  const { id: caseId } = useParams<{ id: string }>();
  const today = new Date().toISOString().slice(0, 10);
  const parties = useAppSelector(getAllPartyPerpetrators);
  const accountProductInfo = useAppSelector(getAccountProductInfo)
  const loanAccountOptions: OptionNameValuePair<string>[] = useMemo(
    () =>
      accountProductInfo.map((account) => ({
        name: account.ext_account_number,
        value: account.loan_reference_id,
      })),
    [accountProductInfo],
  );
  const extAccountNumberById = useMemo(
    () =>
      Object.fromEntries(
        accountProductInfo.map((account) => [
          account.loan_reference_id,
          account.ext_account_number,
        ]),
      ),
    [accountProductInfo],
  );

  const isCreditCase = useAppSelector(isCreditAreaOfFraud);
  
  const events = useAppSelector(selectAllEvents);

  const [selectedStage, setSelectedStage] = useState<{
    name: string;
    value: EventType;
  } | null>(null);
  const [note, setNote] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [caseDate, setCaseDate] = useState<string>("");
  const [selectedPartyIds, setSelectedPartyIds] = useState<string[]>([]);
  const [partyDates, setPartyDates] = useState<Record<string, string>>({});
  const [selectedLoanAccountIds, setSelectedLoanAccountIds] = useState<
    string[]
  >([]);
  const [loanAccountDates, setLoanAccountDates] = useState<
    Record<string, string>
  >({});
  const [selectedLeaIds, setSelectedLeaIds] = useState<string[]>([]);
  const [leaDates, setLeaDates] = useState<Record<string, string>>({});
  const [selectedCommitteeIds, setSelectedCommitteeIds] = useState<string[]>(
    []
  );
  const [committeeDates, setCommitteeDates] = useState<Record<string, string>>(
    {}
  );

  const prevStageValueRef = useRef<EventType | null>(null);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (mode === "Edit" && initialData) {
      if (initialData.eventType) {
        setSelectedStage({
          name: getEventTypeLabel(initialData.eventType),
          value: initialData.eventType,
        });
      }
      if (initialData.note) {
        setNote(initialData.note);
      }

      if (initialData.eventType && initialData.events?.length) {
        const meta = STAGE_META[initialData.eventType];
        if (!meta) return;

        if (meta.scope === "CASE") {
          setCaseDate(initialData.events[0]?.date || today);
          return;
        }

        const ids = initialData.events.map((e) => e.entity_id);
        const dateMap = initialData.events.reduce<Record<string, string>>(
          (acc, e) => {
            acc[e.entity_id] = e.date;
            return acc;
          },
          {}
        );

        switch (meta.entityType) {
          case EventEntityType.PARTY_INVOLVED:
            setSelectedPartyIds(ids);
            setPartyDates(dateMap);
            return;
          case EventEntityType.LOAN_ACCOUNT:
            setSelectedLoanAccountIds(ids);
            setLoanAccountDates(dateMap);
            return;
          case EventEntityType.LEA:
            setSelectedLeaIds(ids);
            setLeaDates(dateMap);
            return;
          case EventEntityType.COMMITTEE:
            setSelectedCommitteeIds(ids);
            setCommitteeDates(dateMap);
            return;
        }
      }
    }
  }, [mode, initialData, today]);

  useEffect(() => {
    const currentStage = selectedStage?.value ?? null;

    if (prevStageValueRef.current === null) {
      prevStageValueRef.current = currentStage;
      return;
    }

    if (prevStageValueRef.current === currentStage) return;

    prevStageValueRef.current = currentStage;

    setCaseDate("");

    setSelectedPartyIds([]);
    setPartyDates({});

    setSelectedLoanAccountIds([]);
    setLoanAccountDates({});

    setSelectedLeaIds([]);
    setLeaDates({});

    setSelectedCommitteeIds([]);
    setCommitteeDates({});

    setNote("");
  }, [selectedStage?.value]);

  const isActiveEvent = (e: any): boolean =>
    e?.status === undefined || e?.status === null || e?.status === "ACTIVE";

  const getExistingEntityIdSet = (
    eventType: EventType,
    entityType: EventEntityType
  ): Set<string> => {
    const set = new Set<string>();
    for (const e of events || []) {
      if (!isActiveEvent(e)) continue;
      if (e.event_type !== eventType) continue;
      if (e.entity_type !== entityType) continue;
      if (typeof e.entity_id === "string") set.add(e.entity_id);
    }
    return set;
  };

  const getAllEntityIdsForType = (entityType: EventEntityType): string[] => {
    switch (entityType) {
      case EventEntityType.PARTY_INVOLVED:
        return parties.map((p) => p.party_id);
      case EventEntityType.LOAN_ACCOUNT:
        return loanAccountOptions.map((option) => option.value);
      case EventEntityType.LEA:
        return Object.values(LEAType);
      case EventEntityType.COMMITTEE:
        return Object.values(CommitteeType);
      case EventEntityType.CASE:
      default:
        return caseId ? [caseId] : [];
    }
  };

  const isStageFullyCompletedForAllEntities = (
    eventType: EventType
  ): boolean => {
    const meta = STAGE_META[eventType];
    if (!meta || meta.scope !== "ENTITY") return false;

    const allIds = getAllEntityIdsForType(meta.entityType);
    if (allIds.length === 0) return false;

    const existing = getExistingEntityIdSet(eventType, meta.entityType);
    return allIds.every((id) => existing.has(id));
  };

  const isCaseStageAlreadyCompleted = (eventType: EventType): boolean => {
    const meta = STAGE_META[eventType];
    if (!meta || meta.scope !== "CASE") return false;
    if (!caseId) return false;

    return (events || []).some(
      (e) =>
        isActiveEvent(e) &&
        e.event_type === eventType &&
        e.entity_type === EventEntityType.CASE &&
        e.entity_id === caseId
    );
  };

  const stageOptions = Object.values(EventType)
    .filter((type) => !SYSTEM_ONLY_EVENT_TYPES.includes(type))
    .filter((type) => !(STAGE_META[type].entityType === EventEntityType.LOAN_ACCOUNT && !isCreditCase))
    .filter((type) => {
      const meta = STAGE_META[type];
      if (!meta) return true;
      if (meta.scope === "CASE") return !isCaseStageAlreadyCompleted(type);
      if (meta.scope === "ENTITY")
        return !isStageFullyCompletedForAllEntities(type);
      return true;
    })
    .map((type) => ({
      name: getEventTypeLabel(type),
      value: type,
    }));

  const selectedMeta = selectedStage ? STAGE_META[selectedStage.value] : null;

  const getActorEmail = (): string | null => {
    const email = auth.user?.profile?.email;
    return typeof email === "string" && email.length ? email : null;
  };

  const buildEventsPayload = (): BulkCaseEventCreateRequest | null => {
    if (!selectedStage || !selectedMeta) return null;
    const actor_email = getActorEmail();
    if (!actor_email) return null;

    if (selectedMeta.scope === "CASE") {
      if (!caseId) return null;
      if (!caseDate) return null;
      return {
        event_type: selectedStage.value,
        events: [
          {
            actor_email,
            entity_type: selectedMeta.entityType,
            entity_id: caseId,
            date: caseDate,
          },
        ],
        notes: note || null,
      };
    }

    const buildEntityEvents = (
      entityType: EventEntityType,
      ids: string[],
      dates: Record<string, string>
    ) => {
      if (ids.length === 0) return null;
      const events = ids
        .map((id) => {
          const date = dates[id];
          if (!date) return null;
          return {
            actor_email,
            entity_type: entityType,
            entity_id: id,
            date,
          };
        })
        .filter(Boolean) as {
        actor_email: string;
        entity_type: EventEntityType;
        entity_id: string;
        date: string;
      }[];

      if (events.length === 0) return null;
      if (events.length !== ids.length) return null;
      return events;
    };

    if (selectedMeta.entityType === EventEntityType.PARTY_INVOLVED) {
      const events = buildEntityEvents(
        EventEntityType.PARTY_INVOLVED,
        selectedPartyIds,
        partyDates
      );
      if (!events) return null;
      return { event_type: selectedStage.value, events, notes: note || null };
    }

    if (selectedMeta.entityType === EventEntityType.LOAN_ACCOUNT) {
      const events = buildEntityEvents(
        EventEntityType.LOAN_ACCOUNT,
        selectedLoanAccountIds,
        loanAccountDates
      );
      if (!events) return null;
      return { event_type: selectedStage.value, events, notes: note || null };
    }

    if (selectedMeta.entityType === EventEntityType.LEA) {
      const events = buildEntityEvents(
        EventEntityType.LEA,
        selectedLeaIds,
        leaDates
      );
      if (!events) return null;
      return { event_type: selectedStage.value, events, notes: note || null };
    }

    if (selectedMeta.entityType === EventEntityType.COMMITTEE) {
      const events = buildEntityEvents(
        EventEntityType.COMMITTEE,
        selectedCommitteeIds,
        committeeDates
      );
      if (!events) return null;
      return { event_type: selectedStage.value, events, notes: note || null };
    }

    return null;
  };

  const canProceed = !!buildEventsPayload() && !isSaving;

  const handleSave = async () => {
    const requestData = buildEventsPayload();
    if (!requestData || !caseId) return;

    try {
      setIsSaving(true);
      await dispatch(
        addCaseEvents({
          case_id: caseId,
          request: requestData,
        })
      );
      onClose();
    } catch (error) {
      console.error("Error saving event:", error);
      setIsSaving(false);
    }
  };

  const renderHeader = () => (
    <div className="flex items-center justify-between">
      <h2 className="text-lg font-semibold leading-[100%] text-[#0A0A0A]">
        {mode === "Add" ? "Add Event" : "Edit Event"}
      </h2>
      <button
        onClick={onClose}
        disabled={isSaving}
        className={cn(
          "text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-sm",
          isSaving && "opacity-50 cursor-not-allowed"
        )}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );

  const renderStageSelect = () => (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium leading-[100%] text-[#0A0A0A]">
        Stage
      </label>

      <SelectDropdownV2
        name="Stage"
        placeholder="Select stage"
        enableSearch={true}
        disabled={isSaving || disableStageSelect}
        options={stageOptions}
        selectedOption={selectedStage ?? undefined}
        onSelectionChange={(opt) => setSelectedStage(opt)}
        containerClassName={cn(
          "h-9 w-full border border-[#E5E5E5] rounded-md bg-white shadow-xs px-3 py-1 flex items-center",
          isSaving &&
            "bg-gray-50 border-gray-200 shadow-none cursor-not-allowed"
        )}
        labelClassName={cn(
          "flex-1 text-sm font-normal leading-5 text-[#0A0A0A] font-sans w-full",
          isSaving && "text-gray-400"
        )}
        focusClassName="outline outline-[2px] outline-indigo-200"
      />
    </div>
  );

  const renderEvents = () =>
    selectedStage ? (
      <>
        <div className="border-t border-[#E5E5E5] w-[560px]" />
        {renderStageSpecificFields()}
      </>
    ) : null;

  const renderDateInput = ({
    value,
    onChange,
    max,
    min,
    disabled,
  }: {
    value: string;
    onChange: (value: string) => void;
    max?: string;
    min?: string;
    disabled?: boolean;
  }) => (
    <div className="relative flex-1 w-[116px] h-full flex items-center">
      <input
        type="date"
        value={value}
        max={max}
        min={min}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Date"
        className={cn(
          "w-full h-full bg-transparent",
          "font-sans font-normal text-sm leading-5",
          "focus:text-[#0A0A0A]",
          "focus:outline-none"
        )}
      />
    </div>
  );

  const renderCaseFields = () => (
    <div className="">
      {renderDateInput({
        value: caseDate || "",
        onChange: (v) => setCaseDate(v),
        max: today,
        disabled: isSaving,
      })}
    </div>
  );

  const getAvailablePartyOptions = (
    parties: { name: string; party_id: string }[],
    selectedStage: { name: string; value: EventType } | null,
    getExistingEntityIdSet: (
      eventType: EventType,
      entityType: EventEntityType
    ) => Set<string>
  ) => {
    if (!selectedStage) {
      return parties.map((p) => ({ name: p.name, value: p.party_id }));
    }

    const existing = getExistingEntityIdSet(
      selectedStage.value,
      EventEntityType.PARTY_INVOLVED
    );

    return parties
      .filter((p) => !existing.has(p.party_id))
      .map((p) => ({ name: p.name, value: p.party_id }));
  };

  const getAvailableLoanAccountOptions = (
    loanAccountOptions: { name: string; value: string }[],
    selectedStage: { name: string; value: EventType } | null,
    getExistingEntityIdSet: (
      eventType: EventType,
      entityType: EventEntityType
    ) => Set<string>
  ) => {
    if (!selectedStage) return loanAccountOptions;

    const existing = getExistingEntityIdSet(
      selectedStage.value,
      EventEntityType.LOAN_ACCOUNT
    );

    return loanAccountOptions.filter((opt) => !existing.has(opt.value));
  };

  const getAvailableLeaOptions = (
    leaOptions: { name: string; value: string }[],
    selectedStage: { name: string; value: EventType } | null,
    getExistingEntityIdSet: (
      eventType: EventType,
      entityType: EventEntityType
    ) => Set<string>
  ) => {
    if (!selectedStage) return leaOptions;

    const existing = getExistingEntityIdSet(
      selectedStage.value,
      EventEntityType.LEA
    );

    return leaOptions.filter((opt) => !existing.has(opt.value));
  };

  const getAvailableOptions = (
    options: OptionNameValuePair<string>[],
    selectedStage: { name: string; value: EventType } | null,
    entityType: EventEntityType,
    getExistingEntityIdSet: (
      eventType: EventType,
      entityType: EventEntityType
    ) => Set<string>
  ) => {
    if (!selectedStage) return options;

    const existing = getExistingEntityIdSet(selectedStage.value, entityType);
    return options.filter((opt) => !existing.has(opt.value));
  };

  const titleCaseEnum = (raw: string): string =>
    raw
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");

  const renderEntityMultiSelectWithDates = ({
    title,
    placeholderLabel,
    options,
    selectedIds,
    setSelectedIds,
    dates,
    setDates,
    getDisplayNameById,
  }: {
    title: string;
    placeholderLabel: string;
    options: OptionNameValuePair<string>[];
    selectedIds: string[];
    setSelectedIds: (ids: string[]) => void;
    dates: Record<string, string>;
    setDates: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    getDisplayNameById: (id: string) => string;
  }) => {
    const rows = selectedIds.map((id) => ({
      id,
      name: getDisplayNameById(id),
      date: dates[id] || "",
    }));

    const isEntityPickerDisabled = isSaving || disableEntitySelect;

    const onChange = (value: string[]) => {
      if (disableEntitySelect) return;
      setSelectedIds(value);
      setDates((prev) => {
        const next: Record<string, string> = {};
        for (const id of value) {
          next[id] = prev[id] ?? "";
        }
        return next;
      });
    };

    return (
      <div className="flex flex-col gap-2">
        <div className="text-sm font-medium leading-5 text-[#737373]">
          {title}
        </div>

        {rows.length > 0 && (
          <div className="flex flex-col gap-2 mt-2">
            {rows.map((row) => (
              <div
                key={row.id}
                className="w-[560px] h-9 flex items-center gap-4"
              >
                <div className="flex-1 min-w-0 text-sm font-normal leading-5 text-[#0A0A0A] truncate">
                  {row.name}
                </div>
                <div className="w-[116px]">
                  {renderDateInput({
                    value: row.date,
                    onChange: (v) =>
                      setDates((prev) => ({ ...prev, [row.id]: v })),
                    max: today,
                    disabled: isSaving,
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="w-full flex flex-row gap-2 items-center">
          <MultiSelectSummaryDropdown<string>
            name="Entities"
            value={selectedIds}
            options={options}
            onChange={onChange}
            placeholder=""
            enableSearch
            disabled={isSaving || disableEntitySelect}
            showChipSummary
            showTooltip
            hideChevronWhenSelected
            rootContainerClassName="max-w-[155px]"
            dropdownClassName="border border-[#E5E5E5] rounded-md"
            renderCustomView={() => (
              <div className="w-full flex items-center gap-2">
                <Plus className="w-4 h-[13.333px] opacity-100 text-[#737373]" />
                <span className="font-sans font-normal text-sm leading-5 text-[#737373]">
                  {placeholderLabel}
                </span>
              </div>
            )}
          />

          <div className="w-[116px] flex flex-row gap-2 items-center">
            <CalendarIcon className="w-4 h-[13.333px] opacity-100 left-[2px] text-[#737373]" />
            <span className="font-sans font-normal text-sm leading-5 tracking-normal align-middle opacity-50 text-[#0A0A0A]">
              Date
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderPartyFields = () =>
    renderEntityMultiSelectWithDates({
      title: "You can add multiple parties",
      placeholderLabel: "Add New Party",
      options: getAvailablePartyOptions(
        parties,
        selectedStage,
        getExistingEntityIdSet
      ),
      selectedIds: selectedPartyIds,
      setSelectedIds: setSelectedPartyIds,
      dates: partyDates,
      setDates: setPartyDates,
      getDisplayNameById: (id) =>
        parties.find((p) => p.party_id === id)?.name || id,
    });

  const renderLoanAccountFields = () =>
    renderEntityMultiSelectWithDates({
      title: "You can add multiple loan accounts",
      placeholderLabel: "Add Loan Account",
      options: getAvailableLoanAccountOptions(
        loanAccountOptions,
        selectedStage,
        getExistingEntityIdSet
      ),
      selectedIds: selectedLoanAccountIds,
      setSelectedIds: setSelectedLoanAccountIds,
      dates: loanAccountDates,
      setDates: setLoanAccountDates,
      getDisplayNameById: (id) => extAccountNumberById[id],
    });

  const leaOptions: OptionNameValuePair<string>[] = Object.values(LEAType).map(
    (v) => ({ name: LEATypeLabelMap[v] ?? titleCaseEnum(v), value: v })
  );

  const renderLeaFields = () =>
    renderEntityMultiSelectWithDates({
      title: "You can add multiple Law Enforcement Agency",
      placeholderLabel: "Add LEA",
      options: getAvailableLeaOptions(
        leaOptions,
        selectedStage,
        getExistingEntityIdSet
      ),
      selectedIds: selectedLeaIds,
      setSelectedIds: setSelectedLeaIds,
      dates: leaDates,
      setDates: setLeaDates,
      getDisplayNameById: (id) =>
        LEATypeLabelMap[id as LEAType] ?? titleCaseEnum(id),
    });

  const committeeOptions: OptionNameValuePair<string>[] = Object.values(
    CommitteeType
  ).map((v) => ({ name: CommitteeTypeLabelMap[v] ?? titleCaseEnum(v), value: v }));

  const renderCommitteeFields = () =>
    renderEntityMultiSelectWithDates({
      title: "You can add multiple Committee",
      placeholderLabel: "Add Committee",
      options: getAvailableOptions(
        committeeOptions,
        selectedStage,
        EventEntityType.COMMITTEE,
        getExistingEntityIdSet
      ),
      selectedIds: selectedCommitteeIds,
      setSelectedIds: setSelectedCommitteeIds,
      dates: committeeDates,
      setDates: setCommitteeDates,
      getDisplayNameById: (id) =>
        CommitteeTypeLabelMap[id as CommitteeType] ?? titleCaseEnum(id),
    });

  const renderUnsupportedEntity = () => (
    <div className="text-sm font-medium leading-5 text-[#737373]">
      This stage type is not supported yet in the modal.
    </div>
  );

  const renderStageSpecificFields = () => {
    if (!selectedStage || !selectedMeta) return null;
    if (selectedMeta.scope === "CASE") return renderCaseFields();
    if (selectedMeta.entityType === EventEntityType.PARTY_INVOLVED)
      return renderPartyFields();
    if (selectedMeta.entityType === EventEntityType.LOAN_ACCOUNT)
      return renderLoanAccountFields();
    if (selectedMeta.entityType === EventEntityType.LEA)
      return renderLeaFields();
    if (selectedMeta.entityType === EventEntityType.COMMITTEE)
      return renderCommitteeFields();
    return renderUnsupportedEntity();
  };

  const renderNote = () => (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium leading-[100%] text-[#0A0A0A]">
          Note
        </label>
        <Info
          className="text-[#737373]"
          style={{
            width: "13.333px",
            height: "13.333px",
            strokeWidth: "1.33px",
          }}
        />
      </div>

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Add a note..."
        maxLength={3000}
        readOnly={isSaving || readOnlyNote}
        className={cn(
          "w-[560px] h-[100px] border border-[#E5E5E5] rounded-md px-3 py-1 text-sm font-normal leading-5 text-[#0A0A0A] placeholder:text-[#737373] transition-colors",
          "resize-none",
          "focus:outline-none focus:ring-2 focus:ring-indigo-200",
          (isSaving || readOnlyNote) &&
            "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
        )}
      />
    </div>
  );

  const renderFooter = () => (
    <ButtonV2
      onClick={handleSave}
      disabled={!canProceed}
      loading={isSaving}
      variant="outline"
      size="md"
      className={cn(
        "flex w-[161px] bg-[#EBEEFE] border-[#3551F3] text-[#3551F3]",
        "hover:bg-[#EBEEFE] hover:text-[#3551F3] self-end"
      )}
      suffix={!isSaving ? <ChevronRight className="h-4 w-4" /> : undefined}
    >
      Save & Proceed
    </ButtonV2>
  );

  return (
    <div className="fixed inset-0 bg-[rgba(10,10,10,0.20)] flex items-center justify-center z-[9999]">
      <div
        className="bg-white border border-[#E5E5E5] rounded-lg shadow-lg p-6 flex flex-col"
        style={{ width: "608px", gap: "16px" }}
      >
        {renderHeader()}
        {renderStageSelect()}
        {renderEvents()}
        {/* {renderNote()} */}
        {renderFooter()}
      </div>
    </div>
  );
};
